import React, { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import usePutUserPageData from '../../usePutUserPageData';
import useCreateUserPageData from '../../useCreateUserPageData';

import { useManageUserModalStore } from './useMangeUserModalStore';
import { ClientOwnerRoleOptions, PestOwnerRoleOptions } from './RoleOptions';

import { UserUpdateFormValues, UserCreateFormValues, userCreateSchema, userUpdateSchema } from 'api/users/types';
import { MLInput } from 'components/Input/Input';
import { MLButton } from 'components/Button/Button';
import useGetUser from 'hooks/VisitReport/useGetUser';
import { MLSelect } from 'components/Select/Select';

import './ManageUserModal.scss';

interface Props {
  refetchUsers: () => void;
  refetchClientUsers: () => void;
}

const ManageUserModal: React.FC<Props> = ({ refetchUsers, refetchClientUsers }) => {
  const { isEdit, isOpen, onManageUserModalClose, userData } = useManageUserModalStore();
  const { data: Me } = useGetUser();
  const { putUserPageData } = usePutUserPageData();
  const { createUserPageData } = useCreateUserPageData();
  const { handleSubmit, control, reset, setError, watch } = useForm<UserCreateFormValues>({
    resolver: zodResolver(isEdit ? userUpdateSchema : userCreateSchema),
    defaultValues: {
      email: '',
      password: '',
      password_check: '',
      first_name: '',
      last_name: '',
      father_name: '',
      phone_number: '',
      role: 'client_company_owner',
      dob: '',
      experience: 0,
      avatar: '',
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (isEdit) {
        reset({
          email: userData?.email || '',
          password: '',
          password_check: '',
          first_name: userData?.first_name || '',
          last_name: userData?.last_name || '',
          father_name: userData?.father_name || '',
          phone_number: userData?.phone_number || '',
          role: userData?.role || 'client_company_owner',
          dob: userData?.dob || '',
          experience: userData?.experience || 0,
          avatar: userData?.avatar || '',
        });
      } else {
        reset({
          email: '',
          password: '',
          password_check: '',
          first_name: '',
          last_name: '',
          father_name: '',
          phone_number: '',
          role: 'client_company_owner',
          dob: '',
          experience: 0,
          avatar: '',
        });
      }
    }
  }, [isOpen, isEdit, userData, reset]);

  const putUserData = (data: UserUpdateFormValues) => {
    const { password, password_check, ...rest } = data;

    const updatedData = {
      ...rest,
      avatar: data.avatar instanceof File ? data.avatar : '',
      role: data.role ?? userData?.role ?? 'client_company_owner',
    };
    putUserPageData(
      { id: userData?.id, userData: updatedData },
      {
        onSuccess: () => {
          enqueueSnackbar('User updated successfully', {
            variant: 'success',
            action: key => (
              <IconButton onClick={() => closeSnackbar(key)} color="inherit">
                <CloseIcon />
              </IconButton>
            ),
          });
          reset();
          onManageUserModalClose();
          refetchUsers();
          refetchClientUsers();
        },
        onError: (error: any) => {
          if (error.response?.data) {
            console.error('API Error Response:', error.response.data);
            const apiErrors = error.response.data;

            Object.keys(apiErrors).forEach(field => {
              setError(field as keyof UserUpdateFormValues, {
                type: 'manual',
                message: apiErrors[field][0],
              });
            });
          }

          enqueueSnackbar('User update failed', { variant: 'error' });
        },
      },
    );
  };

  const createUserData = (data: UserCreateFormValues) => {
    const updatedData = {
      ...data,
      avatar: data.avatar instanceof File ? data.avatar : '',
    };
    createUserPageData(
      { userData: updatedData },
      {
        onSuccess: () => {
          enqueueSnackbar('User created successfully', {
            variant: 'success',
            action: key => (
              <IconButton onClick={() => closeSnackbar(key)} color="inherit">
                <CloseIcon />
              </IconButton>
            ),
          });
          reset();
          onManageUserModalClose();
          refetchUsers();
          refetchClientUsers();
        },
        onError: (error: any) => {
          if (error.response?.data) {
            const apiErrors = error.response.data;

            Object.keys(apiErrors).forEach(field => {
              setError(field as keyof UserCreateFormValues, {
                type: 'manual',
                message: apiErrors[field][0],
              });
            });
          }

          enqueueSnackbar('User update failed', { variant: 'error' });
        },
      },
    );
  };

  const handleClose = () => {
    reset();
    onManageUserModalClose();
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={handleClose} size="lg">
        <ModalHeader className="border-bottom py-3 " toggle={handleClose}>
          <p className="mb-0 text-purple">
            {' '}
            {userData ? `User: ${userData.first_name} ${userData.last_name} edition` : 'Create User'}
          </p>{' '}
        </ModalHeader>
        <ModalBody className="my-3">
          <div className="row">
            <div className="col-xl-6 my-2">
              <Controller
                control={control}
                name="email"
                rules={{
                  required: 'Required field',
                  minLength: { value: 1, message: 'Required field to activate user' },
                }}
                render={({ field, formState: { errors } }) => (
                  <MLInput
                    {...field}
                    label="Email"
                    error={errors?.email?.message}
                    disabled
                    type="string"
                    onChange={e => field.onChange(e.target.value)}
                  />
                )}
              />
            </div>
            <div className="col-xl-6 my-2">
              <Controller
                control={control}
                name="phone_number"
                render={({ field, formState: { errors } }) => (
                  <MLInput
                    {...field}
                    label="Phone number"
                    error={errors?.phone_number?.message}
                    type="string"
                    onChange={e => field.onChange(e.target.value)}
                  />
                )}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-xl-6 my-2">
              <Controller
                control={control}
                name="first_name"
                rules={{
                  required: 'Required field',
                  minLength: { value: 1, message: 'Required field to activate user' },
                }}
                render={({ field, formState: { errors } }) => (
                  <MLInput
                    {...field}
                    label="First name"
                    error={errors?.first_name?.message}
                    required
                    type="string"
                    onChange={e => field.onChange(e.target.value)}
                  />
                )}
              />
            </div>
            <div className="col-xl-6 my-2">
              <Controller
                control={control}
                name="last_name"
                render={({ field, formState: { errors } }) => (
                  <MLInput
                    {...field}
                    label="Last name"
                    error={errors?.last_name?.message}
                    required
                    type="string"
                    onChange={e => field.onChange(e.target.value)}
                  />
                )}
              />
            </div>
          </div>
          <div className="row">
            {/* <div className="col-xl-6 my-2">
              <Controller
                control={control}
                name="experience"
                render={({ field, fieldState: { error } }) => (
                  <MLInput
                    {...field}
                    label="Years of experience"
                    error={error?.message}
                    type="number"
                    onChange={e => {
                      const value = e.target.value;

                      if (value === '' || isNaN(Number(value))) {
                        field.onChange('');
                      } else {
                        field.onChange(Number(value));
                      }
                    }}
                    onBlur={e => {
                      if (e.target.value.trim() === '') {
                        field.onChange(0);
                      }
                    }}
                  />
                )}
              />
            </div> */}
            <div className="col-xl-12 my-2">
              <Controller
                control={control}
                name="dob"
                render={({ field, formState: { errors } }) => (
                  <MLInput
                    {...field}
                    label="Date of birth"
                    error={errors?.dob?.message}
                    type="date"
                    onChange={e => field.onChange(e.target.value)}
                  />
                )}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-xl-6 my-2">
              <Controller
                control={control}
                name="avatar"
                render={({ field: { onChange }, formState: { errors } }) => (
                  <MLInput
                    label="Photo"
                    type="file"
                    onChange={e => {
                      const file = e.target.files?.[0] || null;
                      onChange(file);
                    }}
                    error={errors?.avatar?.message}
                  />
                )}
              />
            </div>
            <div className="col-xl-6 my-2">
              <Controller
                control={control}
                name="role"
                render={({ field }) => (
                  <MLSelect
                    {...field}
                    label="Role"
                    value={field.value}
                    onChange={selectedOption => {
                      field.onChange(selectedOption);
                    }}
                    options={Me?.role === 'pest_client' ? PestOwnerRoleOptions : ClientOwnerRoleOptions}
                    isDisabled
                  />
                )}
              />
            </div>
          </div>
          {!isEdit && (
            <div className="row">
              {/* Password Input */}
              <div className="col-xl-6">
                <Controller
                  control={control}
                  name="password"
                  rules={{
                    required: 'Password is required',
                    minLength: { value: 5, message: 'Password must be at least 5 characters' },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <MLInput {...field} label="Password" type="password" error={error?.message} required />
                  )}
                />
              </div>

              {/* Confirm Password Input */}
              <div className="col-xl-6">
                <Controller
                  control={control}
                  name="password_check"
                  rules={{
                    required: 'Please confirm your password',
                    validate: value => value === watch('password') || 'Passwords do not match',
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <MLInput {...field} label="Confirm password" type="password" error={error?.message} required />
                  )}
                />
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="px-0">
          <div className="w-100 d-flex justify-content-end border-top py-3 mx-0">
            <MLButton
              onClick={() => {
                isEdit ? handleSubmit(putUserData)() : handleSubmit(createUserData)();
              }}
              isGhost
              color="success"
              innerText="Confirm"
            />
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ManageUserModal;
